import {
  ApiError,
  type Customer,
  createTaskSchema,
  queryKeys,
  type TaskDetail,
  updateTaskSchema,
  type Vendor,
} from '@mevaser/shared'
import {
  Badge,
  Button,
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Field,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Textarea,
  useToast,
} from '@mevaser/ui'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { X } from 'lucide-react'
import { useEffect, useState } from 'react'
import { api } from '../lib/api.js'
import { useAuth } from '../lib/auth.js'
import { AddCustomerDialog } from './add-customer-dialog.js'
import { AttachButton, AttachmentChips, useFileUploads } from './attachments.js'
import { type CustomerLite, CustomerPicker } from './customer-picker.js'
import { TicketThread } from './ticket-thread.js'

export interface TaskRow {
  id: string
  taskNumber: string
  title: string
  status: 'open' | 'pending_review' | 'done'
  dueDate: string | null
  startDate: string | null
  assignedStaffId: string | null
  assignedStaffName: string | null
  supportTicketId: string | null
  supportTicketNumber: string | null
  customerId: string | null
  customerName: string | null
  /** Every customer on the task, the primary one first. */
  customers?: { id: string; name: string }[]
  vendorId: string | null
  vendorName: string | null
  rentalId: string | null
  rentalNumber: string | null
  parentTaskId: string | null
  parentTaskNumber: string | null
  openChildCount: number
  completedAt: string | null
  completedByName: string | null
  completionNote: string | null
  createdAt: string
}

/** Radix Select cannot hold an empty string, so "everyone" gets a sentinel. */
export const EVERYONE = '__everyone__'

/** Radix Select cannot hold an empty string, so "no vendor" gets a sentinel. */
export const NO_VENDOR = '__no_vendor__'

interface StaffRow {
  id: string
  name: string
  isActive: boolean
}

/** Today as yyyy-mm-dd in the browser's own time zone — what a date input holds. */
export function todayIso(): string {
  return new Date().toLocaleDateString('en-CA')
}

export function formatDueDate(dueDate: string): string {
  const [y, m, d] = dueDate.split('-').map(Number)
  return new Date(y ?? 0, (m ?? 1) - 1, d ?? 1).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  })
}

/** True while a task's start date is still ahead, so it stays out of the lists (t1348). */
export function isScheduled(task: Pick<TaskRow, 'startDate'>): boolean {
  return Boolean(task.startDate && task.startDate > todayIso())
}

/** "Starts Nov 6" for a task whose start date is still ahead. */
export function StartDate({ task }: { task: Pick<TaskRow, 'startDate'> }) {
  if (!task.startDate || !isScheduled(task)) return null
  return <span className="text-foreground-muted">Starts {formatDueDate(task.startDate)}</span>
}

/** A due date, coloured when an open task is overdue or due today. */
export function DueDate({ task }: { task: Pick<TaskRow, 'dueDate' | 'status'> }) {
  if (!task.dueDate) return <span className="text-foreground-subtle">—</span>
  const today = todayIso()
  const open = task.status === 'open'
  const tone =
    open && task.dueDate < today
      ? 'font-medium text-danger'
      : open && task.dueDate === today
        ? 'font-medium text-warning'
        : 'text-foreground-muted'
  const suffix =
    open && task.dueDate < today ? ' · overdue' : open && task.dueDate === today ? ' · today' : ''
  return (
    <span className={tone}>
      {formatDueDate(task.dueDate)}
      {suffix}
    </span>
  )
}

/** Everyone, yourself, and — for roles that may list staff — each active colleague. */
export function useAssigneeOptions() {
  const { staff, can } = useAuth()
  const canSeeStaff = can('staff:read')
  const { data } = useQuery({
    queryKey: queryKeys.staff.list(),
    queryFn: () => api.get<StaffRow[]>('/staff'),
    enabled: canSeeStaff,
  })
  const colleagues = (data ?? []).filter((s) => s.isActive && s.id !== staff?.id)
  return [
    ...(staff ? [{ id: staff.id, name: `${staff.name} (me)` }] : []),
    ...colleagues.map((s) => ({ id: s.id, name: s.name })),
  ]
}

export function AssigneeSelect({
  value,
  onChange,
}: {
  value: string
  onChange: (value: string) => void
}) {
  const options = useAssigneeOptions()
  return (
    <Field label="Assign to">
      {(props) => (
        <Select value={value} onValueChange={onChange}>
          <SelectTrigger {...props}>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={EVERYONE}>Everyone</SelectItem>
            {options.map((o) => (
              <SelectItem key={o.id} value={o.id}>
                {o.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      )}
    </Field>
  )
}

/** Active vendors for a picker or filter. Listing vendors needs inventory:read,
 *  so roles without it get an empty list and the vendor controls hide. */
export function useVendorOptions() {
  const { can } = useAuth()
  const canSee = can('inventory:read')
  const filters = { isActive: true, pageSize: 200 }
  const { data } = useQuery({
    queryKey: queryKeys.vendors.list(filters),
    queryFn: () => api.get<{ items: Vendor[] }>('/vendors', { query: filters }),
    enabled: canSee,
  })
  return { canSee, vendors: data?.items ?? [] }
}

export function VendorSelect({
  value,
  onChange,
}: {
  value: string
  onChange: (value: string) => void
}) {
  const { canSee, vendors } = useVendorOptions()
  if (!canSee) return null
  return (
    <Field label="Vendor">
      {(props) => (
        <Select value={value} onValueChange={onChange}>
          <SelectTrigger {...props}>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={NO_VENDOR}>No vendor</SelectItem>
            {vendors.map((v) => (
              <SelectItem key={v.id} value={v.id}>
                {v.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      )}
    </Field>
  )
}

/** The task's customers: a removable list (the first is the primary one) and a
 *  picker that adds another. Every search offers to create one, even when it has
 *  matches (customers:write only); the new customer is added here and the task
 *  form around it keeps everything already typed. */
function TaskCustomersField({
  value,
  onChange,
  hint,
}: {
  value: CustomerLite[]
  onChange: (customers: CustomerLite[]) => void
  hint: string
}) {
  const { can } = useAuth()
  const [creatingName, setCreatingName] = useState<string | null>(null)
  const add = (c: Customer | CustomerLite) => {
    if (value.some((v) => v.id === c.id)) return
    onChange([...value, { id: c.id, name: c.name, businessName: c.businessName, phone: c.phone }])
  }
  return (
    <>
      <Field label="Customers" hint={hint}>
        {() => (
          <div className="space-y-2">
            {value.length > 0 ? (
              <ul className="space-y-1">
                {value.map((c, i) => (
                  <li key={c.id} className="flex items-center justify-between gap-2 text-sm">
                    <span className="flex min-w-0 items-center gap-1.5">
                      <span className="truncate font-medium">{c.name}</span>
                      {i === 0 && value.length > 1 ? <Badge tone="neutral">Primary</Badge> : null}
                    </span>
                    <Button
                      variant="ghost"
                      size="icon"
                      aria-label={`Remove ${c.name}`}
                      onClick={() => onChange(value.filter((v) => v.id !== c.id))}
                    >
                      <X className="size-3.5" />
                    </Button>
                  </li>
                ))}
              </ul>
            ) : null}
            <CustomerPicker
              value={null}
              onChange={(c) => c && add(c)}
              placeholder={value.length > 0 ? 'Add another customer…' : undefined}
              onCreate={can('customers:write') ? setCreatingName : undefined}
            />
          </div>
        )}
      </Field>
      <AddCustomerDialog
        open={creatingName !== null}
        onOpenChange={(next) => !next && setCreatingName(null)}
        initialName={creatingName ?? ''}
        onCreated={add}
        existingAction={{ label: 'Use this customer', onSelect: add }}
      />
    </>
  )
}

/** Creates a standalone task, or a follow-up when `supportTicketId` or `parentTask`
 *  is given. A follow-up on a task takes its ticket, customer and vendor from that
 *  task on the server. `customer` / `vendorId` pre-fill the links when opened from
 *  that record's page. */
export function NewTaskDialog({
  open,
  onOpenChange,
  supportTicketId,
  parentTask,
  customer: presetCustomer,
  vendorId: presetVendorId,
  defaultTitle,
  onCreated,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  supportTicketId?: string
  /** Pre-fills the title each time the dialog opens. */
  defaultTitle?: string
  /** Runs with the new task after it is saved, before the dialog closes. */
  onCreated?: (task: TaskRow) => void | Promise<void>
  parentTask?: Pick<
    TaskRow,
    'id' | 'taskNumber' | 'title' | 'customerId' | 'customerName' | 'customers'
  > | null
  customer?: CustomerLite
  vendorId?: string
}) {
  const toast = useToast()
  const queryClient = useQueryClient()
  const [title, setTitle] = useState('')
  const [details, setDetails] = useState('')
  const [assignee, setAssignee] = useState(EVERYONE)
  const [dueDate, setDueDate] = useState('')
  const [startDate, setStartDate] = useState('')
  // A follow-up starts with the customers of what it follows up on (t1369).
  const initialCustomers = (): CustomerLite[] =>
    parentTask
      ? parentTask.customers?.length
        ? parentTask.customers
        : parentTask.customerId
          ? [{ id: parentTask.customerId, name: parentTask.customerName ?? '' }]
          : []
      : presetCustomer
        ? [presetCustomer]
        : []
  const [customers, setCustomers] = useState<CustomerLite[]>(initialCustomers)
  const [vendor, setVendor] = useState(presetVendorId ?? NO_VENDOR)
  const uploads = useFileUploads({ endpoint: '/attachments?for=task' })
  const followUp = Boolean(supportTicketId || parentTask)

  useEffect(() => {
    if (open && defaultTitle) setTitle(defaultTitle)
  }, [open, defaultTitle])

  // biome-ignore lint/correctness/useExhaustiveDependencies: seed when the dialog opens for a record
  useEffect(() => {
    if (open) setCustomers(initialCustomers())
  }, [open, parentTask?.id, presetCustomer?.id])

  // A follow-up added straight from a ticket starts with the ticket's customer and
  // shows the ticket's conversation as read-only context (t1370).
  const fromTicket = Boolean(supportTicketId) && !parentTask
  const { can } = useAuth()
  const { data: ticket } = useQuery({
    queryKey: queryKeys.supportTickets.detail(supportTicketId ?? ''),
    queryFn: () =>
      api.get<{ customerId: string | null; customerName: string | null }>(
        `/support-tickets/${supportTicketId}`,
      ),
    enabled: open && fromTicket && can('support:read'),
  })
  const ticketCustomerId = ticket?.customerId ?? null
  const ticketCustomerName = ticket?.customerName ?? null
  useEffect(() => {
    if (!open || !fromTicket || !ticketCustomerId) return
    setCustomers((current) =>
      current.length > 0 ? current : [{ id: ticketCustomerId, name: ticketCustomerName ?? '' }],
    )
  }, [open, fromTicket, ticketCustomerId, ticketCustomerName])

  const reset = () => {
    uploads.reset()
    setTitle('')
    setDetails('')
    setAssignee(EVERYONE)
    setDueDate('')
    setStartDate('')
    setCustomers(initialCustomers())
    setVendor(presetVendorId ?? NO_VENDOR)
  }

  const create = useMutation({
    mutationFn: async () => {
      const parsed = createTaskSchema.safeParse({
        title,
        details,
        assignedStaffId: assignee === EVERYONE ? null : assignee,
        dueDate: dueDate || null,
        startDate: startDate || null,
        supportTicketId: supportTicketId ?? null,
        parentTaskId: parentTask?.id ?? null,
        customerIds: customers.map((c) => c.id),
        vendorId: followUp || vendor === NO_VENDOR ? null : vendor,
        attachmentIds: uploads.ids,
      })
      if (!parsed.success) {
        throw new Error(parsed.error.issues[0]?.message ?? 'Check the form and try again')
      }
      return api.post<TaskRow>('/tasks', parsed.data)
    },
    onSuccess: async (task) => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.tasks.all() })
      toast.success(followUp ? 'Follow-up added' : 'Task created')
      await onCreated?.(task)
      onOpenChange(false)
      reset()
    },
    onError: (error: Error) =>
      toast.error(
        followUp ? 'Could not add the follow-up' : 'Could not create the task',
        error instanceof ApiError ? error.message : error.message,
      ),
  })

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        onOpenChange(next)
        if (!next) reset()
      }}
    >
      <DialogContent className={fromTicket ? 'max-w-lg' : 'max-w-md'}>
        <DialogHeader>
          <DialogTitle>{followUp ? 'Add follow-up' : 'New task'}</DialogTitle>
          {parentTask ? (
            <DialogDescription>
              Follow-up to <span className="font-identifier">{parentTask.taskNumber}</span> ·{' '}
              {parentTask.title}
            </DialogDescription>
          ) : null}
        </DialogHeader>
        <DialogBody className="space-y-4">
          {fromTicket && supportTicketId && open ? (
            <div className="rounded-md border border-border bg-surface-sunken p-3">
              <TicketThread ticketId={supportTicketId} preview={3} />
            </div>
          ) : null}
          <Field label="Title" required>
            {(props) => (
              <Input
                {...props}
                value={title}
                placeholder={followUp ? 'Call the customer back' : 'Call this supplier'}
                onChange={(e) => setTitle(e.target.value)}
              />
            )}
          </Field>
          <Field label="Details" hint="Paste a picture here to attach it.">
            {(props) => (
              <Textarea
                {...props}
                rows={3}
                value={details}
                onChange={(e) => setDetails(e.target.value)}
                onPaste={uploads.handlePaste}
              />
            )}
          </Field>
          <div className="-mt-2 space-y-2">
            <AttachmentChips uploads={uploads} />
            <AttachButton uploads={uploads} />
          </div>
          <AssigneeSelect value={assignee} onChange={setAssignee} />
          <TaskCustomersField
            value={customers}
            onChange={setCustomers}
            hint={
              followUp
                ? "Filled in from what this follows up on — change it if needed. Also listed on each customer's page in the admin portal; internal only, customers don't see it."
                : "Optional. Also listed on each customer's page in the admin portal — internal only, customers don't see it."
            }
          />
          {followUp ? null : <VendorSelect value={vendor} onChange={setVendor} />}
          <Field
            label="Start date"
            hint="Optional. The task stays out of the task lists until this day."
          >
            {(props) => (
              <Input
                {...props}
                type="date"
                value={startDate}
                max={dueDate || undefined}
                onChange={(e) => setStartDate(e.target.value)}
              />
            )}
          </Field>
          <Field
            label="Due date"
            hint={
              assignee === EVERYONE
                ? 'Everyone is emailed on this day; whoever finishes it closes it for all.'
                : 'The assignee is emailed on this day. Leave blank for no reminder.'
            }
          >
            {(props) => (
              <Input
                {...props}
                type="date"
                value={dueDate}
                onChange={(e) => setDueDate(e.target.value)}
              />
            )}
          </Field>
        </DialogBody>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button variant="primary" loading={create.isPending} onClick={() => create.mutate()}>
            {followUp ? 'Add follow-up' : 'Create task'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

/** Edits a task or follow-up: title, details, who it is assigned to, and due date —
 *  plus the customers.
 *  The list row has no details, so the full task is fetched while the dialog is open. */
export function EditTaskDialog({
  task,
  onOpenChange,
}: {
  task: TaskRow | null
  onOpenChange: (open: boolean) => void
}) {
  const toast = useToast()
  const queryClient = useQueryClient()
  const [title, setTitle] = useState('')
  const [details, setDetails] = useState('')
  const [assignee, setAssignee] = useState(EVERYONE)
  const [dueDate, setDueDate] = useState('')
  const [startDate, setStartDate] = useState('')
  const [customers, setCustomers] = useState<CustomerLite[]>([])
  const uploads = useFileUploads({ endpoint: '/attachments?for=task' })
  const followUp = Boolean(task?.supportTicketId)

  const { data: detail } = useQuery({
    queryKey: queryKeys.tasks.detail(task?.id ?? ''),
    queryFn: () => api.get<TaskDetail>(`/tasks/${task?.id}`),
    enabled: task !== null,
  })

  useEffect(() => {
    if (!task) return
    setTitle(task.title)
    setDetails('')
    setAssignee(task.assignedStaffId ?? EVERYONE)
    setDueDate(task.dueDate ?? '')
    setStartDate(task.startDate ?? '')
    setCustomers(
      task.customers ??
        (task.customerId ? [{ id: task.customerId, name: task.customerName ?? '' }] : []),
    )
  }, [task])

  const detailsLoaded = detail?.id === task?.id
  useEffect(() => {
    if (detailsLoaded) setDetails(detail?.details ?? '')
  }, [detailsLoaded, detail?.details])

  // Seeded once per task: a background refetch must not wipe files just attached.
  // biome-ignore lint/correctness/useExhaustiveDependencies: seed when the task's detail first loads
  useEffect(() => {
    if (detailsLoaded) uploads.reset(detail?.attachments ?? [])
  }, [detailsLoaded, task?.id])

  const save = useMutation({
    mutationFn: async () => {
      const parsed = updateTaskSchema.safeParse({
        title,
        details,
        assignedStaffId: assignee === EVERYONE ? null : assignee,
        dueDate: dueDate || null,
        startDate: startDate || null,
        customerIds: customers.map((c) => c.id),
        attachmentIds: uploads.ids,
      })
      if (!parsed.success) {
        throw new Error(parsed.error.issues[0]?.message ?? 'Check the form and try again')
      }
      return api.patch<TaskDetail>(`/tasks/${task?.id}`, parsed.data)
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.tasks.all() })
      toast.success(followUp ? 'Follow-up updated' : 'Task updated')
      onOpenChange(false)
    },
    onError: (error: Error) =>
      toast.error(
        followUp ? 'Could not update the follow-up' : 'Could not update the task',
        error.message,
      ),
  })

  return (
    <Dialog open={task !== null} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>
            {followUp ? 'Edit follow-up' : 'Edit task'}
            {task ? ` ${task.taskNumber}` : ''}
          </DialogTitle>
        </DialogHeader>
        <DialogBody className="space-y-4">
          <Field label="Title" required>
            {(props) => (
              <Input {...props} value={title} onChange={(e) => setTitle(e.target.value)} />
            )}
          </Field>
          <Field label="Details" hint="Paste a picture here to attach it.">
            {(props) => (
              <Textarea
                {...props}
                rows={3}
                value={details}
                disabled={!detailsLoaded}
                onChange={(e) => setDetails(e.target.value)}
                onPaste={uploads.handlePaste}
              />
            )}
          </Field>
          <div className="-mt-2 space-y-2">
            <AttachmentChips uploads={uploads} />
            <AttachButton uploads={uploads} disabled={!detailsLoaded} />
          </div>
          <AssigneeSelect value={assignee} onChange={setAssignee} />
          <TaskCustomersField
            value={customers}
            onChange={setCustomers}
            hint={`Optional. Also listed on each customer's page in the admin portal — internal only, customers don't see it. Leave empty for none.${task?.parentTaskId || task?.supportTicketId ? ' Started from the task or ticket this follows up on.' : ''}`}
          />
          <Field
            label="Start date"
            hint="Optional. The task stays out of the task lists until this day."
          >
            {(props) => (
              <Input
                {...props}
                type="date"
                value={startDate}
                max={dueDate || undefined}
                onChange={(e) => setStartDate(e.target.value)}
              />
            )}
          </Field>
          <Field
            label="Due date"
            hint={
              assignee === EVERYONE
                ? 'Everyone is emailed on this day; whoever finishes it closes it for all.'
                : 'The assignee is emailed on this day. Leave blank for no reminder.'
            }
          >
            {(props) => (
              <Input
                {...props}
                type="date"
                value={dueDate}
                onChange={(e) => setDueDate(e.target.value)}
              />
            )}
          </Field>
        </DialogBody>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            variant="primary"
            loading={save.isPending}
            disabled={!detailsLoaded}
            onClick={() => save.mutate()}
          >
            Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

/** Complete / reopen / approve / send back. Completing and sending back always
 *  carry a note — the types make a caller without one a compile error. A 409
 *  means a colleague got there first — say so and refresh rather than treating
 *  it as a failure. Staff without `tasks:review` land in "Pending review" on
 *  complete (t1216), which the toast says. */
export function useTaskToggle() {
  const toast = useToast()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (
      input:
        | { id: string; action: 'reopen' | 'approve' }
        | { id: string; action: 'complete' | 'reject'; note: string },
    ) =>
      api.post<TaskRow>(
        `/tasks/${input.id}/${input.action}`,
        'note' in input ? { note: input.note } : {},
      ),
    onSuccess: (task, input) => {
      if (input.action === 'complete' && task.status === 'pending_review') {
        toast.success('Sent for review', 'An administrator will approve it or send it back.')
      } else if (input.action === 'approve') {
        toast.success('Task approved')
      } else if (input.action === 'reject') {
        toast.success('Sent back to open')
      }
    },
    onError: (error: Error) =>
      toast.error(
        error instanceof ApiError && error.status === 409
          ? 'Someone just changed that task'
          : 'Could not update the task',
        error instanceof ApiError ? error.message : undefined,
      ),
    onSettled: () => queryClient.invalidateQueries({ queryKey: queryKeys.tasks.all() }),
  })
}

/** Approve / Send back for a task waiting for review (t1216). Renders nothing
 *  for anyone without `tasks:review` — they get "Reopen" from the caller. */
export function ReviewActions({
  task,
  variant = 'button',
}: {
  task: Pick<TaskRow, 'id' | 'taskNumber' | 'title' | 'status'>
  variant?: 'button' | 'link'
}) {
  const { can } = useAuth()
  const toggle = useTaskToggle()
  const [rejecting, setRejecting] = useState(false)
  if (task.status !== 'pending_review' || !can('tasks:review')) return null

  const approve = () => toggle.mutate({ id: task.id, action: 'approve' })
  return (
    // `contents` keeps the buttons in the caller's flex row; the dialog is a
    // portal whose events still bubble here, so stop them reaching a clickable row.
    // biome-ignore lint/a11y/noStaticElementInteractions: only stops bubbling, not an interactive control
    // biome-ignore lint/a11y/useKeyWithClickEvents: only stops bubbling, not an interactive control
    <span className="contents" onClick={(e) => e.stopPropagation()}>
      {variant === 'link' ? (
        <>
          <button
            type="button"
            className="text-primary hover:underline disabled:opacity-50"
            disabled={toggle.isPending}
            onClick={approve}
          >
            Approve
          </button>
          <button
            type="button"
            className="text-primary hover:underline"
            onClick={() => setRejecting(true)}
          >
            Send back
          </button>
        </>
      ) : (
        <>
          <Button
            variant="ghost"
            size="sm"
            disabled={toggle.isPending}
            onClick={(e) => {
              e.stopPropagation()
              approve()
            }}
          >
            Approve
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={(e) => {
              e.stopPropagation()
              setRejecting(true)
            }}
          >
            Send back
          </Button>
        </>
      )}
      <RejectTaskDialog task={rejecting ? task : null} onClose={() => setRejecting(false)} />
    </span>
  )
}

/** Asks what still needs doing before a task goes back to open. */
export function RejectTaskDialog({
  task,
  onClose,
}: {
  task: Pick<TaskRow, 'id' | 'taskNumber' | 'title'> | null
  onClose: () => void
}) {
  const toggle = useTaskToggle()
  const [note, setNote] = useState('')
  const valid = note.trim().length >= MIN_NOTE_LENGTH

  useEffect(() => {
    if (task) setNote('')
  }, [task])

  const send = () => {
    if (!task || !valid) return
    toggle.mutate(
      { id: task.id, action: 'reject', note: note.trim() },
      {
        onSuccess: onClose,
        // A colleague reviewed it first: nothing left to send back.
        onError: (error) => {
          if (error instanceof ApiError && error.status === 409) onClose()
        },
      },
    )
  }

  return (
    <Dialog open={task !== null} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Send back{task ? ` ${task.taskNumber}` : ''}</DialogTitle>
          {task ? <DialogDescription>{task.title}</DialogDescription> : null}
        </DialogHeader>
        <DialogBody className="space-y-4">
          <Field
            label="What still needs doing"
            required
            hint="Saved to the task's history and sent to whoever marked it done."
          >
            {(props) => (
              <Textarea
                {...props}
                rows={3}
                value={note}
                autoFocus
                placeholder="The invoice is still missing"
                onChange={(e) => setNote(e.target.value)}
              />
            )}
          </Field>
        </DialogBody>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="primary" disabled={!valid} loading={toggle.isPending} onClick={send}>
            Send back
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

/** The prompt shown by every "Mark done": completion is blocked until the
 *  note is written. The note is kept on the task and in its history. */
export function CompleteTaskDialog({
  task,
  onClose,
}: {
  task: TaskRow | null
  onClose: () => void
}) {
  const { can } = useAuth()
  const toggle = useTaskToggle()
  const [note, setNote] = useState('')
  const valid = note.trim().length >= MIN_NOTE_LENGTH
  // Without tasks:review the task waits for an administrator before it counts (t1216).
  const needsReview = !can('tasks:review')

  useEffect(() => {
    if (task) setNote('')
  }, [task])

  const finish = () => {
    if (!task || !valid) return
    toggle.mutate(
      { id: task.id, action: 'complete', note: note.trim() },
      {
        onSuccess: onClose,
        // A colleague got there first: nothing left to complete, so don't hold the note hostage.
        onError: (error) => {
          if (error instanceof ApiError && error.status === 409) onClose()
        },
      },
    )
  }

  return (
    <Dialog open={task !== null} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Mark done{task ? ` ${task.taskNumber}` : ''}</DialogTitle>
          {task ? <DialogDescription>{task.title}</DialogDescription> : null}
        </DialogHeader>
        <DialogBody className="space-y-4">
          {task && task.openChildCount > 0 ? (
            <p className="rounded-md border border-warning/40 bg-warning/10 px-3 py-2 text-sm">
              This task still has {task.openChildCount} open follow-up
              {task.openChildCount === 1 ? '' : 's'}.{' '}
              {task.openChildCount === 1 ? 'It stays' : 'They stay'} open — complete{' '}
              {task.openChildCount === 1 ? 'it' : 'them'} separately.
            </p>
          ) : null}
          {needsReview ? (
            <p className="rounded-md border border-border bg-surface-sunken px-3 py-2 text-sm">
              An administrator reviews this before it counts as done.
            </p>
          ) : null}
          <Field
            label="What was done"
            required
            hint="Saved to the task's history and shown on the ticket."
          >
            {(props) => (
              <Textarea
                {...props}
                rows={3}
                value={note}
                autoFocus
                placeholder="Called the customer back and sorted it out"
                onChange={(e) => setNote(e.target.value)}
              />
            )}
          </Field>
        </DialogBody>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="primary" disabled={!valid} loading={toggle.isPending} onClick={finish}>
            {needsReview ? 'Send for review' : 'Mark done'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

/** Keep in step with `completeTaskSchema` in @mevaser/shared. */
const MIN_NOTE_LENGTH = 3

export type ThreadedTask = TaskRow & {
  /** 0 for a top-level item, +1 per generation below it. */
  depth: number
  /** A follow-up whose parent is not in this list (filtered out, or on a later page). */
  orphan: boolean
}

/** Arranges tasks as threads: each parent is followed by its follow-ups, and
 *  theirs, in the order the server returned them. Only parents present in
 *  `rows` can adopt children; a child without its parent here stays top-level
 *  and is flagged `orphan` so the UI can say what it follows up on. */
export function groupTaskThreads(rows: TaskRow[]): ThreadedTask[] {
  const ids = new Set(rows.map((r) => r.id))
  const children = new Map<string, TaskRow[]>()
  const roots: TaskRow[] = []
  for (const row of rows) {
    if (row.parentTaskId && ids.has(row.parentTaskId)) {
      const siblings = children.get(row.parentTaskId) ?? []
      siblings.push(row)
      children.set(row.parentTaskId, siblings)
    } else {
      roots.push(row)
    }
  }
  const out: ThreadedTask[] = []
  const seen = new Set<string>()
  const walk = (row: TaskRow, depth: number, orphan: boolean) => {
    if (seen.has(row.id)) return
    seen.add(row.id)
    out.push({ ...row, depth: Math.min(depth, MAX_THREAD_DEPTH), orphan })
    for (const child of children.get(row.id) ?? []) walk(child, depth + 1, false)
  }
  for (const root of roots) walk(root, 0, root.parentTaskId !== null)
  return out
}

/** Deeper threads still nest, but stop indenting so they stay readable. */
const MAX_THREAD_DEPTH = 4
